import { path } from '../../../../../libs'
import { message } from 'antd'
import * as React from 'react'
import { WithdrawalEdit } from '../../edit'
import { CopyOutlined } from '@ant-design/icons'
import { DividendBalance, TransactionProduct } from '../../edit/types'
import { transactionRequestDestinationType } from '../../../const'


type Props = {
  withdrawal: WithdrawalEdit
  dividendBalance: DividendBalance
}

const Money = ({
  value,
  currency,
}: {
  value?: string | number
  currency?: string
}) => {
  if (!value || !currency) return null
  const num = typeof value === 'number' ? value : parseFloat(value)
  if (Number.isNaN(num)) return null
  const numberStr = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 }).format(num)
  return (
    <>
      {numberStr}
      <span style={{ fontSize: '0.6em', fontWeight: 400, marginLeft: 3, opacity: 0.7 }}>{currency}</span>
    </>
  )
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--crm-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 10,
}

const cardStyle: React.CSSProperties = {
  background: 'var(--crm-bg-elevated)',
  border: '1px solid var(--crm-border)',
  borderRadius: 8,
  padding: 14,
}

const dividerStyle: React.CSSProperties = {
  border: 'none',
  borderTop: '1px solid var(--crm-border)',
  margin: '16px 0',
}

const copyBtnStyle: React.CSSProperties = {
  marginLeft: 6,
  cursor: 'pointer',
  fontSize: 16,
  color: 'var(--crm-text-secondary)',
  verticalAlign: 'middle',
}

export const ClientCard = ({ withdrawal, dividendBalance }: Props) => {
  // resolve destination data
  let destinationId = String(withdrawal.destinationId)
  let balance = withdrawal.client.accounts.find(
    (w) => w.accountId === withdrawal.destinationId,
  )?.balance
  let availableToWithdraw = withdrawal.client.accounts.find(
    (w) => w.accountId === withdrawal.destinationId,
  )?.availableToWithdraw
  let currency = withdrawal.client.accounts.find(
    (w) => w.accountId === withdrawal.destinationId,
  )?.currency

  const isWallet =
    withdrawal.destinationType === transactionRequestDestinationType.wallet() ||
    withdrawal.destinationType === 'wallet'

  const isExchanger =
    // @ts-ignore
    (withdrawal.client?.exchanger?.id === withdrawal.destinationId ||
      withdrawal.gateway?.exchanger?.id) &&
    withdrawal.destinationType === 'exchanger'

  const account = !isWallet && !isExchanger
    ? withdrawal.client.accounts.find((a) => a.accountId === withdrawal.destinationId)
    : null

  if (isWallet) {
    const wallet = withdrawal.client.wallets.find(
      (w) => w.walletId === withdrawal.destinationId,
    )
    balance = wallet?.balance
    currency = wallet?.currency
  } else if (isExchanger) {
    destinationId = 'Exchanger'
    balance =
      withdrawal.client?.exchanger?.balance ||
      withdrawal.gateway?.exchanger?.balance
    currency = 'USD'
  }

  const currentOriginHost = window.location.origin
  const handleCopyClientInfo = () => {
    const clientUrl = `${currentOriginHost}/client/${withdrawal.client.clientId}`
    const plainText = `
Client name: ${withdrawal.client.fullName}
Client ID: ${withdrawal.client.clientId}
Email: ${withdrawal.client.email}
`.trim()

    const htmlText = `
<b>Client name:</b> ${withdrawal.client.fullName}
<b>Client ID:</b> <a href="${clientUrl}">${withdrawal.client.clientId}</a>
<b>Email:</b> ${withdrawal.client.email}
`.trim()

    navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlText], { type: 'text/html' }),
      }),
    ])
    message.success('Client info copied!')
  }

  const handleCopyClientId = () => {
    navigator.clipboard.writeText(withdrawal.client.clientId)
    message.success('Client ID copied!')
  }

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(withdrawal.client.email)
    message.success('Email copied!')
  }

  return (
    <>
      {/* ── CLIENT SECTION ── */}
      <div>
        <div style={sectionTitleStyle}>
          Client
          <CopyOutlined
            onClick={handleCopyClientInfo}
            style={{ marginLeft: 8, cursor: 'pointer', fontSize: 11 }}
          />
        </div>
        <div style={cardStyle}>
          {/* Name */}
          <div style={{ fontSize: 20, fontWeight: 600, fontFamily: 'monospace', marginBottom: 4 }}>
            {withdrawal.client.fullName}
          </div>

          {/* Email */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, wordBreak: 'break-all' }}>
            <span style={{ fontSize: 14, color: 'var(--crm-text-secondary)', fontFamily: 'monospace' }}>
              {withdrawal.client.email}
            </span>
            <CopyOutlined onClick={handleCopyEmail} style={copyBtnStyle} />
          </div>

          {/* ClientId + verified */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <a
                href={path.clients.details(withdrawal.client.clientId)}
                target="_blank"
                style={{ fontSize: 20, fontFamily: 'monospace', fontWeight: 600 }}
              >
                {withdrawal.client.clientId} ↗
              </a>
              <CopyOutlined onClick={handleCopyClientId} style={copyBtnStyle} />
            </div>
            {withdrawal.client.verified ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: 'var(--crm-accent-green, #22c55e)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--crm-accent-green, #22c55e)' }} />
                Verified
              </span>
            ) : (
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--crm-text-secondary)', textTransform: 'uppercase' }}>
                Unverified
              </span>
            )}
          </div>

          {/* Country */}
          {withdrawal.client.country && (
            <div style={{ fontSize: 14, color: 'var(--crm-text-secondary)', marginBottom: 4 }}>
              {withdrawal.client.country}
            </div>
          )}

          {(withdrawal.client.isTest || withdrawal.client.isPremium) && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {withdrawal.client.isTest && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'rgba(168,85,247,0.1)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.3)' }}>
                  Test
                </span>
              )}
              {withdrawal.client.isPremium && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
                  Premium
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <hr style={dividerStyle} />

      {/* ── WALLET / ACCOUNT SECTION ── */}
      <div>
        <div style={sectionTitleStyle}>
          {isWallet ? 'Wallet' : isExchanger ? 'Exchanger' : 'Account'}
        </div>
        <div style={cardStyle}>
          <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'monospace', marginBottom: 8 }}>
            {account
              ? `${account.accountPlatform} / ${account.mtLogin}${account.currency ? ` · ${account.currency}` : ''}`
              : destinationId}
          </div>
          <div style={{ fontFamily: 'monospace', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div>
              <div style={{ fontSize: 14, color: 'var(--crm-text-secondary)', marginBottom: 2 }}>Balance</div>
              <div style={{ fontWeight: 600, fontSize: 20, color: isWallet ? 'var(--crm-accent-green, #22c55e)' : undefined }}>
                <Money value={balance as string} currency={currency} />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: 'var(--crm-text-secondary)', marginBottom: 2 }}>Available</div>
              <div style={{ fontWeight: !isWallet ? 600 : 500, fontSize: 20, color: !isWallet ? 'var(--crm-accent-green, #22c55e)' : undefined }}>
                {availableToWithdraw ? (
                  <Money value={availableToWithdraw as string} currency={currency} />
                ) : (
                  <span style={{ color: 'var(--crm-text-secondary)' }}>—</span>
                )}
              </div>
            </div>
          </div>
          {withdrawal.product === TransactionProduct.nova && dividendBalance && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 14, color: 'var(--crm-text-secondary)', marginBottom: 2 }}>Dividend balance</div>
              <div style={{ color: 'var(--crm-accent-green, #22c55e)', fontWeight: 600, fontFamily: 'monospace' }}>
                <Money value={dividendBalance.balance as string} currency={dividendBalance.currency} />
              </div>
            </div>
          )}
        </div>
        <a
          target="_blank"
          href={`${path.billing.deposit.entry()}?clientId=${withdrawal.client.clientId}`}
          style={{ display: 'inline-block', fontSize: 14, marginTop: 8 }}
        >
          View deposits ↗
        </a>
      </div>

      {withdrawal.clientAmount && withdrawal.clientCurrency && (
        <>
          <hr style={dividerStyle} />
          <div>
            <div style={sectionTitleStyle}>Client amount</div>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'monospace', color: 'var(--crm-accent-green, #22c55e)' }}>
              <Money value={withdrawal.clientAmount} currency={withdrawal.clientCurrency} />
            </div>
          </div>
        </>
      )}
    </>
  )
}
